import React from "react";

interface PennyItemsDisclaimerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  retailer?: string;
}

const PennyItemsDisclaimerModal: React.FC<PennyItemsDisclaimerModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  retailer,
}) => {
  if (!isOpen) return null;
  const isDollarGeneral = retailer === "dollar_general";

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full">
                <span className="text-lg">💰</span>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Penny Items Disclaimer
              </h3>
            </div>
          </div>

          {/* Content */}
          <div className="mb-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-amber-600 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-amber-800 mb-1">
                    Important Notice
                  </h4>
                  <p className="text-sm text-amber-700">
                    {isDollarGeneral ? (
                      <>Dollar General changed these products to its <strong>$0.00 penny marker</strong>. Register price and availability still need to be verified in the store.</>
                    ) : (
                      <>These items are <strong>predictions</strong> and are not 100% confirmed to be in stock or at this price.</>
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3 text-sm text-gray-600">
              <p>
                Penny items are products that may be marked down to $0.01 as
                part of store clearance processes. However, please note:
              </p>

              <ul className="space-y-2 ml-4">
                <li className="flex items-start space-x-2">
                  <span className="text-gray-400 mt-1">•</span>
                  <span>
                    {isDollarGeneral
                      ? "The app and register may disagree, and employees may remove or decline to sell an item"
                      : "Availability and pricing are predictions based on historical data"}
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-gray-400 mt-1">•</span>
                  <span>Items may not be in stock at your local store</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-gray-400 mt-1">•</span>
                  <span>
                    Actual prices may vary and are subject to store policies
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onAccept}
              className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 rounded-lg transition-all duration-200 shadow-sm"
            >
              I Understand
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PennyItemsDisclaimerModal;
